import React from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const PaginationV2 = ({
  current_page,
  last_page,
  next_page_url,
  prev_page_url,
}) => {
  const navigate = useNavigate();

  // Get pageSize from URL params, default to 30
  const getPageSizeFromUrl = () => {
    const url = new URL(window.location);
    const size = parseInt(url.searchParams.get("pageSize"), 10);
    return [30, 60, 100, 200].includes(size) ? size : 30;
  };
  const pageSize = getPageSizeFromUrl();

  const handlePrevPage = () => {
    if (prev_page_url) {
      const url = new URL(window.location);
      url.searchParams.set("page", current_page - 1);
      navigate(url.pathname + url.search);
    }
  };

  const handleNextPage = () => {
    if (next_page_url) {
      const url = new URL(window.location);
      url.searchParams.set("page", current_page + 1);
      navigate(url.pathname + url.search);
    }
  };

  const handlePageClick = (page) => {
    const url = new URL(window.location);
    url.searchParams.set("page", page);
    navigate(url.pathname + url.search);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    const url = new URL(window.location);
    url.searchParams.set("pageSize", newSize);
    url.searchParams.set("page", 1); // Reset to first page
    navigate(url.pathname + url.search);
  };

  // Generate page numbers array with ellipsis logic
  const getPageNumbers = () => {
    const pages = [];
    if (last_page <= 7) {
      for (let i = 1; i <= last_page; i++) pages.push(i);
    } else {
      // Always show first, last, current, and 2 before/after current
      const left = Math.max(2, current_page - 2);
      const right = Math.min(last_page - 1, current_page + 2);
      pages.push(1);
      if (left > 2) pages.push("left-ellipsis");
      for (let i = left; i <= right; i++) pages.push(i);
      if (right < last_page - 1) pages.push("right-ellipsis");
      pages.push(last_page);
    }
    return pages;
  };
  const pageNumbers = getPageNumbers();

  const handleFirstPage = () => {
    const url = new URL(window.location);
    url.searchParams.set("page", 1);
    navigate(url.pathname + url.search);
  };

  const handleLastPage = () => {
    const url = new URL(window.location);
    url.searchParams.set("page", last_page);
    navigate(url.pathname + url.search);
  };

  return (
    <nav
      className="flex items-center flex-col gap-2 md:flex-row justify-between p-4"
      aria-label="Pagination"
    >
      <div className="flex items-center space-x-2 mt-2 md:mt-0">
        <label htmlFor="pageSize" className="text-sm text-gray-700">
          Show
        </label>
        <select
          id="pageSize"
          value={pageSize}
          onChange={handlePageSizeChange}
          className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {[30, 60, 100, 200].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <span className="text-sm text-gray-700">items per page</span>
      </div>
      <div className="flex items-center space-x-2">
        {/* First page button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleFirstPage}
          disabled={current_page === 1}
          className={`inline-flex items-center h-8 w-8 justify-center text-sm border border-gray-300 font-medium rounded-md ${
            current_page === 1
              ? "text-gray-400 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-50"
          }`}
          aria-label="First page"
        >
          <ChevronDoubleLeftIcon className="w-5 h-5" />
        </motion.button>
        {/* Prev page button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handlePrevPage}
          disabled={!prev_page_url}
          className={`inline-flex items-center h-8 w-8 justify-center text-sm border border-gray-300 font-medium rounded-md ${
            !prev_page_url
              ? "text-gray-400 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </motion.button>

        <div className="flex items-center space-x-1">
          {pageNumbers.map((page, idx) =>
            typeof page === "number" ? (
              <motion.button
                key={page}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handlePageClick(page)}
                className={`inline-flex items-center w-8 h-8 justify-center aspect-square text-sm   font-medium rounded-md ${
                  current_page === page
                    ? "bg-bluePrimary text-white"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {page}
              </motion.button>
            ) : (
              <span key={page + idx} className="px-2 text-gray-400 select-none">
                &hellip;
              </span>
            )
          )}
        </div>

        {/* Next page button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleNextPage}
          disabled={!next_page_url}
          className={`inline-flex items-center h-8 w-8 justify-center text-sm border border-gray-300 font-medium rounded-md ${
            !next_page_url
              ? "text-gray-400 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          <ChevronRightIcon className="w-5 h-5" />
        </motion.button>
        {/* Last page button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleLastPage}
          disabled={current_page === last_page}
          className={`inline-flex items-center h-8 w-8 justify-center text-sm border border-gray-300 font-medium rounded-md ${
            current_page === last_page
              ? "text-gray-400 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-50"
          }`}
          aria-label="Last page"
        >
          <ChevronDoubleRightIcon className="w-5 h-5" />
        </motion.button>
      </div>
    </nav>
  );
};

export default PaginationV2;
